// Environmental encounters
//
// Optional `climate` field scopes an encounter to a climate so the roll can
// suppress inappropriate hazards (see rollEnvironmentalEncounter). Vocabulary:
//   'hot'  - hot/arid campaigns (desert theme, snow tiles excluded)
//   'cold' - cold campaigns (snow theme)
//   'any'  - or simply untagged: fires everywhere (default, backwards-compatible)
// Leave genuinely universal hazards untagged so older saves keep working.
//
// Optional `setting` field scopes an encounter to open-air vs enclosed interiors so
// weather/sky hazards can be suppressed underground (see rollEnvironmentalEncounter).
// Vocabulary:
//   'outdoor'  - open-air only: filtered out inside enclosed sites (caves, mountains)
//   'interior' - enclosed interiors only
//   'any'      - or simply untagged: fires everywhere (default, backwards-compatible)
// Leave hazards that read fine both above and below ground untagged (e.g. earthquake,
// which becomes a cave-in underground).

export const ENVIRONMENTAL_ENCOUNTERS = {
  'sudden_storm': {
    name: 'Sudden Storm',
    icon: '⛈️',
    encounterTier: 'immediate',
    environmental: true,
    setting: 'outdoor',
    description: 'Dark clouds gather with unnatural speed and a violent storm breaks overhead!',
    image: '/assets/encounters/sudden_storm.webp',
    difficulty: 'medium',
    suggestedActions: [
      { label: 'Find Shelter', skill: 'Survival', description: 'Locate cover quickly' },
      { label: 'Press On', skill: 'Constitution', description: 'Endure the storm' },
      { label: 'Make Camp', skill: 'Survival', description: 'Set up emergency shelter' },
      { label: 'Arcane Shield', skill: 'Arcana', description: 'Use magic for protection' }
    ],
    rewards: { xp: 30, gold: '0', items: ['rainwater:70%', 'storm_crystal:20%'] },
    consequences: {
      criticalSuccess: 'You find excellent shelter and the storm uncovers hidden treasures.',
      success: 'You find a bank to crouch under and the storm passes over you, loud but harmless.',
      failure: 'The storm soaks your equipment and slows travel.',
      criticalFailure: 'Lightning hits close enough to throw you down. You get up burned and ringing, and part of the baggage did not survive the wet.'
    }
  },

  'thick_fog': {
    name: 'Unnatural Fog',
    icon: '🌫️',
    encounterTier: 'narrative',
    environmental: true,
    setting: 'outdoor',
    narrativeHook: 'a wall of thick fog rolling across the land',
    aiContext: 'Dense fog blankets the area, reducing visibility to nearly nothing. Strange sounds echo through the mist. The fog might be natural, magical, or hiding something.',
    description: 'Impenetrable fog rolls in, reducing visibility to mere feet. Strange sounds echo in the whiteness.',
    image: '/assets/encounters/thick_fog.webp',
    difficulty: 'easy',
    suggestedActions: [
      { label: 'Navigate', skill: 'Survival', description: 'Try to maintain direction' },
      { label: 'Wait', skill: null, description: 'Let the fog pass' },
      { label: 'Listen', skill: 'Perception', description: 'Identify sounds in the fog' },
      { label: 'Dispel', skill: 'Arcana', description: 'Clear the fog magically' }
    ],
    rewards: { xp: 25, gold: '0', items: ['fog_essence:40%', 'traveler_map:30%'] },
    consequences: {
      criticalSuccess: 'You navigate perfectly and find something hidden by the fog.',
      success: 'You make it through without incident.',
      failure: 'You get turned around and lose time.',
      criticalFailure: 'You stumble into danger hidden by the fog.'
    }
  },

  'earthquake': {
    name: 'Earthquake',
    icon: '🌋',
    encounterTier: 'immediate',
    environmental: true,
    description: 'The ground shakes violently as an earthquake strikes!',
    image: '/assets/encounters/earthquake.webp',
    difficulty: 'hard',
    suggestedActions: [
      { label: 'Drop and Cover', skill: 'Acrobatics', description: 'Protect yourself from debris' },
      { label: 'Find Open Ground', skill: 'Athletics', description: 'Get away from structures' },
      { label: 'Stabilize Footing', skill: 'Acrobatics', description: 'Keep your balance' },
      { label: 'Help Others', skill: 'Athletics', description: 'Assist party members' }
    ],
    rewards: { xp: 50, gold: '0', items: ['exposed_minerals:50%', 'uncovered_ruins:25%', 'fallen_treasure:30%'] },
    consequences: {
      criticalSuccess: 'You keep everyone on their feet, and when the shaking stops a crack in the ground has opened on something worth taking.',
      success: 'You weather the earthquake without injury.',
      failure: 'Falling stone catches you before you can get clear, and you come away bruised.',
      criticalFailure: 'You fall into a fissure and must be rescued.'
    }
  },

  'heat_wave': {
    name: 'Scorching Heat',
    icon: '🔥',
    encounterTier: 'narrative',
    environmental: true,
    climate: 'hot',
    setting: 'outdoor',
    narrativeHook: 'the air shimmering with intense heat',
    aiContext: 'An oppressive heat wave makes travel dangerous. Dehydration and heat exhaustion threaten the party. Finding water and shade becomes critical.',
    description: 'The sun beats down mercilessly, the air so hot it shimmers. Every step is exhausting.',
    image: '/assets/encounters/heat_wave.webp',
    difficulty: 'medium',
    suggestedActions: [
      { label: 'Find Water', skill: 'Survival', description: 'Search for a water source' },
      { label: 'Travel at Night', skill: 'Survival', description: 'Rest during the day' },
      { label: 'Create Shade', skill: 'Survival', description: 'Improvise sun protection' },
      { label: 'Push Through', skill: 'Constitution', description: 'Endure the heat' }
    ],
    rewards: { xp: 30, gold: '0', items: ['herbal_remedy:60%', 'desert_flower:30%'] },
    consequences: {
      criticalSuccess: 'You find an oasis with cool water and shade.',
      success: 'You rest through the worst of the day in what shade there is and move again at evening.',
      failure: 'The heat wrings you out, and by afternoon you are stopping every few hundred paces.',
      criticalFailure: 'Someone collapses from heat exhaustion.'
    }
  },

  'cold_snap': {
    name: 'Bitter Cold',
    icon: '❄️',
    encounterTier: 'narrative',
    environmental: true,
    climate: 'cold',
    setting: 'outdoor',
    narrativeHook: 'the air turning brutally, bone-deep cold',
    aiContext: 'A savage cold snap descends on the party. Frostbite and hypothermia threaten anyone caught in the open. Finding shelter and warmth becomes critical.',
    description: 'The temperature plummets and a biting wind cuts through every layer. Each breath fogs and freezes.',
    image: '/assets/encounters/cold_snap.webp',
    difficulty: 'medium',
    suggestedActions: [
      { label: 'Find Shelter', skill: 'Survival', description: 'Locate cover from the wind' },
      { label: 'Build a Fire', skill: 'Survival', description: 'Kindle warmth against the cold' },
      { label: 'Bundle Up', skill: 'Survival', description: 'Improvise extra insulation' },
      { label: 'Push Through', skill: 'Constitution', description: 'Endure the freezing cold' }
    ],
    rewards: { xp: 30, gold: '0', items: ['herbal_remedy:60%', 'frost_flower:30%'] },
    consequences: {
      criticalSuccess: 'You find a sheltered hollow with dry wood and a warming spring.',
      success: 'You keep moving and keep the blood going, and the cold does not get a grip.',
      failure: 'The cold gets into fingers and feet, and every mile takes longer than the last.',
      criticalFailure: 'Someone succumbs to the cold and risks frostbite.'
    }
  },

  'strange_lights': {
    name: 'Strange Lights',
    icon: '✨',
    encounterTier: 'narrative',
    environmental: true,
    setting: 'outdoor',
    narrativeHook: 'mysterious lights dancing on the horizon',
    aiContext: 'Unexplained lights appear in the sky or floating above the ground. They could be will-o-wisps, magical phenomena, or signals from other travelers.',
    description: 'Eerie lights dance in the distance, their colors shifting and their movement almost purposeful.',
    image: '/assets/encounters/strange_lights.webp',
    difficulty: 'easy',
    suggestedActions: [
      { label: 'Follow', skill: 'Survival', description: 'Track the lights to their source' },
      { label: 'Study', skill: 'Arcana', description: 'Analyze the magical nature' },
      { label: 'Signal Back', skill: 'Survival', description: 'Try to communicate' },
      { label: 'Ignore', skill: null, description: 'Continue on your way' }
    ],
    rewards: { xp: 35, gold: '1d20', items: ['wisp_essence:40%', 'ancient_knowledge:50%', 'quest_clue:30%'] },
    consequences: {
      criticalSuccess: 'The lights lead you to treasure or helpful allies.',
      success: 'You discover the lights are a natural phenomenon and gain knowledge.',
      failure: 'The lights lead nowhere interesting.',
      criticalFailure: 'The lights were leading you somewhere on purpose, and by the time you understand that you are far from any path you know.'
    }
  }

};
